import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

/**
 * A spoken conversation, in real time.
 *
 * The audio goes straight from the browser to OpenAI over WebRTC. It does not
 * pass through our server, and that is not an optimisation — a round trip
 * through Fly would add the half second that turns a conversation back into a
 * walkie-talkie. What the server does is mint a short-lived key and write the
 * instructions; the talking is peer to peer.
 *
 * Turn-taking is decided by the model's own voice activity detection, so there
 * is no button. You stop talking, it answers. You can interrupt it and it stops.
 *
 * Nothing is saved from here. The conversation produces a transcript, the
 * transcript is offered as a DRAFT, and the person reviews and edits before any
 * record exists. The model conducts the interview; it does not write the record.
 */

type Phase = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

export interface Turn {
  who: 'you' | 'assistant';
  text: string;
}

export function TalkLive({
  formatKey,
  mode = 'record',
  onTypeInstead,
  onTranscript,
  onExit,
}: {
  formatKey: string;
  /**
   * `record` interviews to fill a thought record. `support` just talks — there
   * is nothing to complete and nothing is drafted from it afterwards.
   */
  mode?: 'record' | 'support';
  /**
   * Where "Type instead" goes in support mode.
   *
   * Without it the button called onExit, which ends the conversation and says
   * nothing was saved — so it named an alternative and delivered a dead end.
   * The record flow needs no such thing: there, onExit really does hand back
   * the typed form, which is what the words say.
   */
  onTypeInstead?: () => void;
  /** The whole conversation, once it ends. Never saved directly. */
  onTranscript: (turns: Turn[]) => void;
  onExit: () => void;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [speaking, setSpeaking] = useState(false);
  // Set by the app's own lexical check, never by the model. Once raised it
  // stays raised: a later, calmer sentence does not unsay the earlier one.
  const [crisis, setCrisis] = useState<
    { name: string; contact: string; description: string; available: string }[] | null
  >(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Turns are appended from data-channel events, which arrive faster than React
  // state settles; a ref keeps the transcript whole when the call ends.
  const turnsRef = useRef<Turn[]>([]);
  // Read inside data-channel callbacks, where React state is stale.
  const speakingRef = useRef(false);
  const endingRef = useRef(false);
  // A finish_conversation that arrived while a question was still hanging.
  const pendingFinishRef = useRef(false);
  const finishRef = useRef<() => void>(() => {});

  const addTurn = useCallback((turn: Turn) => {
    if (turn.text.trim() === '') return;
    turnsRef.current = [...turnsRef.current, turn];
    setTurns(turnsRef.current);
  }, []);

  const teardown = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
  }, []);

  // The microphone must not outlive the screen, and a WebRTC connection left
  // open keeps billing.
  useEffect(() => () => teardown(), [teardown]);

  const start = useCallback(async () => {
    setPhase('connecting');
    setError(null);
    try {
      const session = await api.realtimeSession(formatKey, mode);

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // Its voice.
      pc.ontrack = (e) => {
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0] ?? null;
          void audioRef.current.play().catch(() => {});
        }
      };

      /*
       * The microphone side. The model's own noise reduction is set on the
       * session and does a different job — this cleans what leaves the device,
       * that cleans what the model listens to, and both matter on a phone in a
       * room with a television on.
       *
       * Mono at 24kHz because that is what the realtime pipeline uses: sending
       * stereo at 48 means the browser resamples and downmixes anyway, and doing
       * it here rather than there keeps echo cancellation working on the signal
       * that is actually transmitted.
       */
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 24000,
        },
      });
      streamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      /*
       * Close once it has stopped speaking.
       *
       * `speaking` is state and would be stale inside this closure, so the
       * audio-buffer events drive a ref instead. Polling is crude but it is the
       * only signal available here, and the alternative — cutting the line the
       * moment the tool call lands — talks over its own goodbye.
       */
      const endWhenQuiet = (): void => {
        if (endingRef.current) return;
        endingRef.current = true;
        const tick = window.setInterval(() => {
          if (!speakingRef.current) {
            window.clearInterval(tick);
            finishRef.current();
          }
        }, 250);
        // Never hang on a stuck audio buffer.
        window.setTimeout(() => {
          window.clearInterval(tick);
          if (endingRef.current) finishRef.current();
        }, 12000);
      };

      const channel = pc.createDataChannel('oai-events');

      /*
       * Ask it to speak first.
       *
       * Turn detection only fires a response when it hears YOU, so on its own
       * the call opens in silence and waits — which reads as broken, and means
       * the first thing that happens is you saying "hello?" to a machine that
       * invited you to talk. One `response.create` on connect and it opens the
       * conversation the way the instructions tell it to.
       */
      /*
       * Ask for the opening turn once the SESSION exists, not once the pipe does.
       *
       * `onopen` fires when the data channel is up, which is before the server
       * has finished creating the session behind it. A `response.create` sent
       * into that gap is answered by a session that does not have the
       * instructions yet, or is dropped — and the symptom is exactly what was
       * reported: you press the button, it connects, and then nothing happens
       * until you say "hello?" to a machine that invited you to talk.
       *
       * `session.created` is the server saying it is ready. The timer is a
       * fallback for the case where that event never arrives, so a changed event
       * name upstream degrades to the old behaviour rather than to silence.
       */
      let opened = false;
      const openTurn = (): void => {
        if (opened || channel.readyState !== 'open') return;
        opened = true;
        channel.send(JSON.stringify({ type: 'response.create' }));
      };
      channel.onopen = () => {
        window.setTimeout(openTurn, 1500);
      };

      channel.onmessage = (e) => {
        let msg: {
          type?: string;
          transcript?: string;
          name?: string;
          item?: { type?: string; name?: string };
          error?: { message?: string };
        };
        try {
          msg = JSON.parse(e.data as string) as typeof msg;
        } catch {
          return;
        }
        // Ready. This is the moment the instructions are in place.
        if (msg.type === 'session.created' || msg.type === 'session.updated') openTurn();

        /*
         * And say so when it fails.
         *
         * Every unrecognised event was dropped on the floor, `error` among them,
         * so a session that refused a request looked identical to one thinking
         * about its answer — you sit in silence with no way to tell whether it
         * is coming. Surfaced rather than logged: this screen is the only place
         * a person would find out.
         */
        if (msg.type === 'error') {
          const detail = msg.error?.message ?? 'the voice service refused the request';
          setError(`The conversation could not start — ${detail}. You can go back to typing.`);
          return;
        }

        // What you said, as it heard it.
        if (msg.type === 'conversation.item.input_audio_transcription.completed' && msg.transcript) {
          addTurn({ who: 'you', text: msg.transcript });
          // Checked server-side against the app's own patterns. Independent of
          // whatever the model decides to say back.
          void api
            .safetyCheck(msg.transcript)
            .then((r) => {
              if (r.level !== 'none' && r.resources.length > 0) setCrisis(r.resources);
            })
            .catch(() => {});
        }
        // What it said back. Kept for context when drafting, and so the screen
        // can show the conversation rather than a blank panel.
        if (msg.type === 'response.output_audio_transcript.done' && msg.transcript) {
          addTurn({ who: 'assistant', text: msg.transcript });
        }
        if (msg.type === 'output_audio_buffer.started') { speakingRef.current = true; setSpeaking(true); }
        if (msg.type === 'output_audio_buffer.stopped') { speakingRef.current = false; setSpeaking(false); }

        /*
         * It ends the call itself.
         *
         * Two event shapes carry the same thing depending on how the response
         * was streamed, and missing it means the conversation just stops dead
         * with the person waiting — so both are handled.
         *
         * Deferred until it has finished speaking. Tearing the connection down
         * the instant the tool call arrives cuts off its own closing sentence
         * mid-word, which is a worse ending than the button was.
         */
        const finished =
          (msg.type === 'response.function_call_arguments.done' && msg.name === 'finish_conversation') ||
          (msg.type === 'response.output_item.done' &&
            msg.item?.type === 'function_call' &&
            msg.item?.name === 'finish_conversation');

        /*
         * Refuse to hang up on an unanswered question.
         *
         * It asked "so where does that leave you?" and called the finish tool in
         * the same turn, so the call ended before the answer — and the answer to
         * that one is the whole point of the exercise. The instructions now
         * forbid it, but a prompt is a request and this is the one place where
         * losing the turn loses the thing they came to say.
         *
         * So the tool call is held rather than obeyed: if the last thing said was
         * a question from it, finishing waits until they have replied. Their next
         * turn releases it.
         */
        if (finished) {
          const last = turnsRef.current[turnsRef.current.length - 1];
          const awaitingAnswer = last?.who === 'assistant' && /\?\s*$/.test(last.text.trim());
          if (awaitingAnswer) pendingFinishRef.current = true;
          else endWhenQuiet();
        }

        // Their reply landed, so a held finish can now go ahead.
        if (
          pendingFinishRef.current &&
          msg.type === 'conversation.item.input_audio_transcription.completed' &&
          msg.transcript?.trim()
        ) {
          pendingFinishRef.current = false;
          endWhenQuiet();
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const answer = await fetch(
        `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(session.model)}`,
        {
          method: 'POST',
          body: offer.sdp ?? '',
          headers: {
            Authorization: `Bearer ${session.value}`,
            'Content-Type': 'application/sdp',
          },
        },
      );
      if (!answer.ok) throw new Error(`handshake failed (${answer.status})`);
      await pc.setRemoteDescription({ type: 'answer', sdp: await answer.text() });

      setPhase('live');
    } catch (e) {
      teardown();
      const name = e instanceof DOMException ? e.name : '';
      setError(
        name === 'NotAllowedError'
          ? 'Microphone access was blocked. You can allow it in your browser settings, or go back to typing.'
          : e instanceof ApiError
            ? e.message
            : "Couldn't start the conversation.",
      );
      setPhase('error');
    }
  }, [formatKey, mode, addTurn, teardown]);

  // The data-channel callbacks close over the first render, so they reach the
  // current finish through a ref rather than a stale copy of it.
  finishRef.current = (): void => finish();

  function finish(): void {
    teardown();
    setPhase('ended');
    onTranscript(turnsRef.current);
  }

  return (
    <div className="stack-lg stack talk">
      {/* Its voice. Not user-visible; controls live below. */}
      <audio ref={audioRef} autoPlay hidden />

      <div className="row-between">
        <span className="faint">Talking it through</span>
        {/* "Type instead" is only true in ONE of the two modes.
            Building a record, onExit hands you back the typed form — the
            sentence describes exactly what happens. In support mode there is no
            form: onExit ends the conversation and says nothing was saved, so
            the button promised somewhere to type and delivered a dead end.
            There is a typed companion on the server, but nothing in this client
            has ever called it, so the honest label is the one that says what
            the button does. */}
        <button
          className="btn btn-ghost btn-compact"
          onClick={() => {
            teardown();
            if (onTypeInstead) onTypeInstead();
            else onExit();
          }}
        >
          {mode === 'support' && !onTypeInstead ? 'Not now' : 'Type instead'}
        </button>
      </div>

      {phase === 'idle' && (
        <div className="stack">
          <div className="talk-question">
            <h1>Ready when you are</h1>
            <p className="muted">
              {mode === 'support'
                ? "Just talk. It won't ask you to fill anything in, nothing is saved from this, and you can stop whenever you like."
                : "It'll ask about what happened and you can just talk. No buttons while you're speaking — it waits for you to finish, then asks the next thing. Nothing is saved until you've read it over."}
            </p>
          </div>
          <button className="btn btn-primary btn-lg btn-block" onClick={() => void start()}>
            Start talking
          </button>
        </div>
      )}

      {phase === 'connecting' && (
        <div className="row" style={{ justifyContent: 'center', gap: 'var(--space-3)' }}>
          <span className="spinner" />
          <span className="muted">Connecting…</span>
        </div>
      )}

      {/* Raised by the app, not by the conversation. Placed above everything so
          it is not something to scroll past, and it does not end the call —
          hanging up on someone at that moment would be its own kind of harm. */}
      {crisis && (
        <div className="banner banner-caution stack-sm stack">
          <strong>This matters more than the rest of this.</strong>
          <p>
            If you are thinking about hurting yourself, please talk to someone who can help right
            now.
          </p>
          {crisis.slice(0, 3).map((r) => (
            <p key={r.name}>
              <strong>{r.name}</strong> · {r.contact}
              <br />
              <span className="faint">{r.description}</span>
            </p>
          ))}
          <a className="btn btn-primary btn-block" href="/sos">
            Open Help now
          </a>
        </div>
      )}

      {error && <div className="banner banner-caution">{error}</div>}

      {phase === 'error' && (
        <div className="stack">
          <button className="btn btn-primary btn-lg btn-block" onClick={() => void start()}>
            Try again
          </button>
          {/* Failing to connect is the single most likely moment for someone to
              want the typed route, so it must be the offer here rather than a
              way out. */}
          <button className="btn btn-ghost btn-block" onClick={onTypeInstead ?? onExit}>
            {mode === 'support' && !onTypeInstead ? 'Leave it' : 'Type instead'}
          </button>
        </div>
      )}

      {phase === 'live' && (
        <>
          <div className={speaking ? 'talk-orb talk-orb-speaking' : 'talk-orb'} aria-hidden="true" />
          <p className="talk-state" aria-live="polite">
            {speaking ? 'speaking' : 'listening'}
          </p>

          {/* The conversation so far, so there is something to look at and a way
              to notice a mishearing early. Newest last, like a chat. */}
          {turns.length > 0 && (
            <div className="stack-sm stack talk-log">
              {turns.map((t, i) => (
                <p key={i} className={t.who === 'you' ? 'talk-said talk-said-you' : 'talk-said'}>
                  {t.text}
                </p>
              ))}
            </div>
          )}

          <button className="btn btn-secondary btn-block" onClick={finish}>
            {mode === 'support' ? "I'm done" : "That's everything"}
          </button>
        </>
      )}
    </div>
  );
}
